import { TChatbotSettingsLayout } from '~/features/settings/models'
import { Keys } from '~/features/ui/models'
import { ECognitoGroups } from '~/models/auth'

export enum EBrandColorType {
  menuBackground = 'menuBackground',
  menuFont = 'menuFont',
  menuIcon = 'menuIcon',
  mainBackground = 'mainBackground',
  mainFont = 'mainFont',
  primary1 = 'primary1',
  primary2 = 'primary2',
  secondary1 = 'secondary1',
  secondary2 = 'secondary2',
  tertiary1 = 'tertiary1',
  tertiary2 = 'tertiary2',
  tertiary3 = 'tertiary3',
  tertiary4 = 'tertiary4',
  tertiary5 = 'tertiary5',
  primaryButtonBg = 'primaryButtonBg',
  primaryButtonFont = 'primaryButtonFont',
  secondaryButtonBg = 'secondaryButtonBg',
  secondaryButtonFont = 'secondaryButtonFont',
  tertiaryButtonBg = 'tertiaryButtonBg',
  tertiaryButtonFont = 'tertiaryButtonFont',
  errorMain = 'errorMain',
  demoArrowColor = 'demoArrowColor',

  'onboarding.mainFont' = 'onboarding.mainFont',
  'onboarding.mainBg' = 'onboarding.mainBg',
  'onboarding.tryItOut.titleFont' = 'onboarding.tryItOut.titleFont',
  'onboarding.tryItOut.dottedLineBorder' = 'onboarding.tryItOut.dottedLineBorder',
  'onboarding.tryItOut.mainButtonBg' = 'onboarding.tryItOut.mainButtonBg',
  'onboarding.tryItOut.mainButtonFont' = 'onboarding.tryItOut.mainButtonFont',
  'onboarding.tryItOut.secondaryButtonBg' = 'onboarding.tryItOut.secondaryButtonBg',
  'onboarding.tryItOut.secondaryButtonFont' = 'onboarding.tryItOut.secondaryButtonFont',
  'onboarding.tryItOut.primaryCtaFont' = 'onboarding.tryItOut.primaryCtaFont',
  'onboarding.tryItOut.ctaBoxesBg' = 'onboarding.tryItOut.ctaBoxesBg',
  'onboarding.tryItOut.ctaBoxesAndIconFont' = 'onboarding.tryItOut.ctaBoxesAndIconFont',
  'onboarding.tryItOut.secondaryFont' = 'onboarding.tryItOut.secondaryFont',
  'onboarding.tryItOut.tertiaryFont' = 'onboarding.tryItOut.tertiaryFont',
  'onboarding.tryItOut.linkFont' = 'onboarding.tryItOut.linkFont',
}

export type TBrandColor = {
  id: string
  type: EBrandColorType
  color: string
}

export type TPartner = {
  partnerName: string
  showBrandingToggle: boolean
  showCharacterToggle: boolean
  domain: string
  defaultAgentName: string
  defaultService: string
  brandUIColors: TBrandColor[] | null
  customHeaderLinks: TPartnerHeaderLinks[] | null
  socialLinks: TPartnerSocialLinks[] | null
  showEmbeddedChat: boolean
  chatAgentId: string
  chatTemplateColors: TChatbotSettingsLayout | null
  billingEmail: string
  supportEmail: string
  notificationEmail: string
  emailDomain: string
  partnerType: EPartnerType
  pricing: TPartnerPricing[] | null
  siteImages: TSiteImages[] | null
  id: string
  createdDate: string
  updatedDate: string
  partnerRedirectLink: string
  partnerUpgradeMessage: string
  trackingEmail: string
  collectionEmail: string
  purchaseLink: string
  paymentMethodId: string
  demoLink: string
  productName: string
  allowedQueryParams: string[]
  customerAuthType: ECustomerAuthType
  legalName: string
  legalAddress: string
  stateOfIncorporation: string
  partnerRedirectLinkParams: TPartnerRedirectLinkParams[] | null
}

export enum ECustomerAuthType {
  internal = 'internal',
  sso = 'sso',
}

export enum EPartners {
  heyLibby = 'heyLibby',
  virtualGurus = 'Virtual Gurus',
}

export enum ESiteImagesTypes {
  portalNavigationLogo = 'portalNavigationLogo',
  logoLight = 'logoLight',
  logoDark = 'logoDark',
  favicon = 'favicon',
  buttonIcon = 'buttonIcon',
  ogImage = 'ogImage',
}

export const friendlySiteImagesTypes: { [x in ESiteImagesTypes]: string } = {
  [ESiteImagesTypes.portalNavigationLogo]: 'Portal Navigation Logo',
  [ESiteImagesTypes.logoLight]: 'Logo Light',
  [ESiteImagesTypes.logoDark]: 'Logo Dark',
  [ESiteImagesTypes.favicon]: 'Favicon',
  [ESiteImagesTypes.buttonIcon]: 'Button Icon',
  [ESiteImagesTypes.ogImage]: 'OG Image',
}

export const friendlySiteImagesDescription: {
  [x in ESiteImagesTypes]: string
} = {
  [ESiteImagesTypes.portalNavigationLogo]:
    'Image displayed in the side navigation drawer. Recommended size: 200x50px',
  [ESiteImagesTypes.logoLight]:
    'Image used in various places on the site, when the background is a darker color. Recommended size: 200x50px',
  [ESiteImagesTypes.logoDark]:
    'Image used in various places on the site, when the background is a lighter color. Recommended size: 200x50px',
  [ESiteImagesTypes.favicon]:
    'This is the icon that appears next to the name of the site in the browser tab. Recommended size: 32x32px',
  [ESiteImagesTypes.buttonIcon]:
    'This is the icon that appears in the chatbot, on the `Chat now` button for the Link view layout, and on above the textfield on the Simple layout. Recommended size: 32x32px',
  [ESiteImagesTypes.ogImage]: 'Image displayed in the Open Graph meta tags',
}

export type TSiteImages = {
  id: string
  type: ESiteImagesTypes
  imageUrl: string
}
export type TFormSiteImage = {
  file: File | undefined
  id: string
  type: ESiteImagesTypes
  imageUrl: string | undefined
}
export enum EPartnerType {
  none = '',
  default = 'default',
}

export enum EPartnerSocialLinksPlatform {
  facebook = 'facebook',
  x = 'x',
  linkedin = 'linkedin',
  instagram = 'instagram',
  tiktok = 'tiktok',
}

export type TPartnerSocialLinks = {
  id: string
  platform: EPartnerSocialLinksPlatform
  url: string
}

export type TPartnerHeaderLinks = {
  id: string
  label: string
  url: string
}

export type TPartnerPricing = {
  id: string
  tier: ECognitoGroups
  stripeProductId: string
  stripePriceId: string
}

export type TPartnerRedirectLinkParams = {
  key: string
  source: string
  value: string
}

export const partnerKeys: Keys<TPartner> = {
  partnerName: 'partnerName',
  showBrandingToggle: 'showBrandingToggle',
  showCharacterToggle: 'showCharacterToggle',
  domain: 'domain',
  defaultAgentName: 'defaultAgentName',
  defaultService: 'defaultService',
  brandUIColors: 'brandUIColors',
  customHeaderLinks: 'customHeaderLinks',
  socialLinks: 'socialLinks',
  showEmbeddedChat: 'showEmbeddedChat',
  chatAgentId: 'chatAgentId',
  chatTemplateColors: 'chatTemplateColors',
  billingEmail: 'billingEmail',
  supportEmail: 'supportEmail',
  notificationEmail: 'notificationEmail',
  emailDomain: 'emailDomain',
  partnerType: 'partnerType',
  pricing: 'pricing',
  siteImages: 'siteImages',
  id: 'id',
  createdDate: 'createdDate',
  updatedDate: 'updatedDate',
  partnerRedirectLink: 'partnerRedirectLink',
  partnerUpgradeMessage: 'partnerUpgradeMessage',
  trackingEmail: 'trackingEmail',
  collectionEmail: 'collectionEmail',
  purchaseLink: 'purchaseLink',
  paymentMethodId: 'paymentMethodId',
  demoLink: 'demoLink',
  productName: 'productName',
  allowedQueryParams: 'allowedQueryParams',
  customerAuthType: 'customerAuthType',
  legalName: 'legalName',
  legalAddress: 'legalAddress',
  stateOfIncorporation: 'stateOfIncorporation',
  partnerRedirectLinkParams: 'partnerRedirectLinkParams',
}

export const partnerHeaderLinksKeys: Keys<TPartnerHeaderLinks> = {
  id: 'id',
  label: 'label',
  url: 'url',
}

export const partnerPricingKeys: Keys<TPartnerPricing> = {
  id: 'id',
  tier: 'tier',
  stripeProductId: 'stripeProductId',
  stripePriceId: 'stripePriceId',
}

export enum EPartnerDomains {
  staging = 'staging.mylibby.ai',
  whiteLabelChris = 'whitelabel.chrisannapoc.com',
  prod = 'heylibby.ai',
  virtualGurus = 'thevirtualgurus.ai',
}

export type TPartnerGeneralSettingsForm = { id: string } & Partial<TPartner>

export enum EPartnerRedirectLinkParamsSource {
  static = 'static',
  userEmail = 'user_email',
}
